import { motion } from 'framer-motion';
import { Building2, Shield, Stethoscope, Landmark, Eye, Lock, Globe } from 'lucide-react';

const categories = [
  {
    name: 'Owners',
    example: 'e.g. ADHDS',
    icon: Globe,
    color: 'bg-amber-500',
    light: 'bg-amber-50 border-amber-200',
    accent: 'text-amber-700',
    desc: 'Governing entities with full visibility across the entire community. Can see data across all facilities, organizations, and user activities.',
    scope: 'Full community-wide access',
    scopeIcon: Eye,
    scopeColor: 'bg-amber-100 text-amber-700 border-amber-300',
    facilities: ['All facilities in community'],
    wide: true,
  },
  {
    name: 'Providers',
    example: 'e.g. Hospitals, Clinics',
    icon: Stethoscope,
    color: 'bg-teal-600',
    light: 'bg-teal-50 border-teal-200',
    accent: 'text-teal-700',
    desc: 'Healthcare provider entities in the market — hospitals, clinics, labs, pharmacies. Access is scoped to their own assigned facilities only.',
    scope: 'Own facilities only',
    scopeIcon: Lock,
    scopeColor: 'bg-teal-100 text-teal-700 border-teal-300',
    facilities: ['Rashid Hospital', 'Rashid Pharmacy'],
  },
  {
    name: 'Payers',
    example: 'e.g. Insurance, TPA',
    icon: Shield,
    color: 'bg-blue-600',
    light: 'bg-blue-50 border-blue-200',
    accent: 'text-blue-700',
    desc: 'Insurance companies or Third Party Administrators (TPAs). Access is scoped to their own assigned facilities — does not extend to others.',
    scope: 'Own facilities only',
    scopeIcon: Lock,
    scopeColor: 'bg-blue-100 text-blue-700 border-blue-300',
    facilities: ['ADNIC Claims Center'],
  },
  {
    name: 'Regulators',
    example: 'e.g. DOH, MOHAP',
    icon: Landmark,
    color: 'bg-violet-600',
    light: 'bg-violet-50 border-violet-200',
    accent: 'text-violet-700',
    desc: 'Regulatory entities overseeing compliance and standards. Access is scoped to their own assigned facilities — does not extend to others.',
    scope: 'Own facilities only',
    scopeIcon: Lock,
    scopeColor: 'bg-violet-100 text-violet-700 border-violet-300',
    facilities: ['Regulatory Office A'],
  },
];

export default function FacilityCategoryScene() {
  return (
    <div data-testid="scene-facility-category" className="flex flex-col items-center justify-start h-full px-8 pt-3 overflow-y-auto">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="w-full max-w-5xl">
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-6">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-body font-medium bg-teal-600/10 text-teal-700 mb-2">
            <Building2 size={14} /> Facility Categories
          </span>
          <h2 className="font-heading font-bold text-3xl tracking-tight text-[#0A0A0A]">Facility Categories</h2>
          <p className="mt-1.5 font-body text-sm text-[#737373] max-w-2xl mx-auto">
            Four types of entities participate in the Shafafiya community — each with a different scope of data visibility
          </p>
        </motion.div>

        {/* Owner - full width highlight */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl border-2 border-amber-300 shadow-xl p-5 mb-4"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-amber-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/20">
              <Globe size={26} className="text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-0.5">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A]">Owners</h3>
                <span className="text-[11px] font-body text-amber-600 px-2 py-0.5 rounded-full bg-amber-100 border border-amber-200">e.g. ADHDS</span>
              </div>
              <p className="font-body text-sm text-[#737373] leading-relaxed">{categories[0].desc}</p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="flex-shrink-0 px-4 py-2.5 rounded-xl bg-amber-100 border border-amber-300 text-center"
            >
              <Eye size={20} className="text-amber-600 mx-auto mb-1" />
              <p className="font-heading font-bold text-[11px] text-amber-700">Full Community Access</p>
              <p className="text-[10px] font-body text-amber-600">All facilities &bull; All data</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Divider with scope difference */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="flex items-center gap-3 my-3 px-2"
        >
          <div className="flex-1 h-[1px] bg-[#E5E7EB]" />
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FAFAFA] border border-[#E5E7EB]">
            <Lock size={12} className="text-[#737373]" />
            <span className="text-[11px] font-body font-medium text-[#737373]">Scoped access below — own facilities only</span>
          </div>
          <div className="flex-1 h-[1px] bg-[#E5E7EB]" />
        </motion.div>

        {/* Provider, Payer, Regulator - 3 column */}
        <div className="grid grid-cols-3 gap-4 mb-4">
          {categories.slice(1).map((cat, i) => (
            <motion.div
              key={cat.name}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 + i * 0.2 }}
              className={`bg-white rounded-2xl border shadow-lg overflow-hidden ${cat.light}`}
            >
              <div className={`${cat.color} px-4 py-3 flex items-center gap-2.5`}>
                <cat.icon size={20} className="text-white" />
                <div>
                  <h3 className="font-heading font-bold text-base text-white">{cat.name}</h3>
                  <p className="text-[10px] font-body text-white/70">{cat.example}</p>
                </div>
              </div>
              <div className="p-4">
                <p className="font-body text-[12px] text-[#737373] leading-relaxed mb-3">{cat.desc}</p>

                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${cat.scopeColor} mb-3`}>
                  <Lock size={13} />
                  <span className="text-[11px] font-body font-bold">{cat.scope}</span>
                </div>

                <p className="text-[10px] font-body font-bold text-[#0A0A0A] mb-1.5">Example facilities:</p>
                <div className="space-y-1">
                  {cat.facilities.map(f => (
                    <div key={f} className="flex items-center gap-1.5 text-[11px] font-body text-[#737373]">
                      <div className={`w-1.5 h-1.5 rounded-full ${cat.color}`} />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Key difference callout */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6 }}
          className="bg-[#FAFAFA] rounded-xl border border-[#E5E7EB] p-4 mb-4"
        >
          <p className="font-heading font-bold text-sm text-[#0A0A0A] mb-2">Key Difference</p>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-start gap-3 bg-white rounded-lg border border-[#E5E7EB] p-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Eye size={14} className="text-white" />
              </div>
              <div>
                <p className="font-body font-bold text-[12px] text-[#0A0A0A]">Owners (ADHDS)</p>
                <p className="text-[11px] font-body text-[#737373]">See <span className="font-bold text-amber-600">across all facilities</span> in the entire community — full visibility</p>
              </div>
            </div>
            <div className="flex items-start gap-3 bg-white rounded-lg border border-[#E5E7EB] p-3">
              <div className="w-8 h-8 rounded-lg bg-[#737373] flex items-center justify-center flex-shrink-0 mt-0.5">
                <Lock size={14} className="text-white" />
              </div>
              <div>
                <p className="font-body font-bold text-[12px] text-[#0A0A0A]">Providers / Payers / Regulators</p>
                <p className="text-[11px] font-body text-[#737373]">See only <span className="font-bold text-[#0A0A0A]">within their own facilities</span> — access to multiple facilities shows data for those facilities only, does not extend to others</p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
